import React, { useState, useMemo, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { Helmet } from "react-helmet"
import { slugify } from "../../../utils/slugify"
import initPmReveal from "../../../utils/pmReveal"
import {
  getRouteFacts,
  getSiblingRoutes,
  getInboundRoutes,
  isTier1Route,
  titleCase,
} from "../../../utils/routeData"
import { routePath, cityPath, SITE_ORIGIN } from "../../../utils/urls"
import cityContentOverrides from "../../../data/cityContentOverrides.json"
import WeatherChip from "../../../components/WeatherChip"
import "./cityPairView.scss"
import "./route-premium.scss"

// Small stroke-icon helper (currentColor).
const Ic = ({ d, sw = 1.8 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
)
const I = {
  pin: <Ic d={<><path d="M12 21s-7-6.6-7-11a7 7 0 0 1 14 0c0 4.4-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>} />,
  clock: <Ic d={<><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" /></>} />,
  rupee: <Ic d={<path d="M7 5h10M7 9h10M15 5c0 5-4 6-8 6l7 8" />} />,
  road: <Ic d={<path d="M4 21 8 3M20 21 16 3M12 5v3M12 12v3M12 19v1" />} />,
  user: <Ic d={<><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></>} />,
  headset: <Ic d={<><path d="M4 13v-1a8 8 0 0 1 16 0v1" /><rect x="2.5" y="13" width="4" height="6" rx="2" /><rect x="17.5" y="13" width="4" height="6" rx="2" /><path d="M20 19a4 4 0 0 1-4 3h-2" /></>} />,
  shield: <Ic d={<><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" /></>} />,
  coffee: <Ic d={<><path d="M4 8h13v5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8Z" /><path d="M17 9h2a2 2 0 0 1 0 6h-2M6 2v2M10 2v2M14 2v2" /></>} />,
  file: <Ic d={<><path d="M6 2h9l4 4v16H6z" /><path d="M14 2v5h5M9 13h6M9 17h6" /></>} />,
  arrow: <Ic d={<><path d="M5 12h14M13 6l6 6-6 6" /></>} sw={2.2} />,
  compass: <Ic d={<><circle cx="12" cy="12" r="9" /><path d="M16 8l-2 6-6 2 2-6 6-2Z" /></>} />,
  swap: <Ic d={<path d="M7 4v13M4 14l3 3 3-3M17 20V7M14 10l3-3 3 3" />} />,
}

const FARE_INCLUDES = [
  { k: "user", t: "Driver allowance", d: "The driver's allowance is already in the fare shown. Nothing extra to settle on the road." },
  { k: "file", t: "5% GST included", d: "Government GST is built into the price you see. The fare is the fare." },
  { k: "shield", t: "No hidden charges", d: "You pay the fixed price shown upfront. Only tolls, parking and state permits are extra, at actuals." },
  { k: "coffee", t: "Breaks on the way", d: "Quick refreshment and rest stops on the route are free. Off-route side-trips simply add distance at the per-km rate shown." },
]

// House style: no em/en dashes anywhere. Replace them in rendered content
// (spaced dashes become a comma; bare ones a hyphen) recursively over any
// sections object from the CMS or the baked overrides.
const deDash = (s) =>
  typeof s === "string" ? s.replace(/\s*[—–]\s*/g, ", ") : s
const deepClean = (o) =>
  Array.isArray(o)
    ? o.map(deepClean)
    : o && typeof o === "object"
      ? Object.fromEntries(Object.entries(o).map(([k, v]) => [k, deepClean(v)]))
      : deDash(o)

const CityPairView = ({
  fromCity,
  toCity,
  fromCityName,
  toCityName,
  fromCityData,
  toCityData,
  loading,
}) => {
  const navigate = useNavigate()
  const { distance } = useSelector((state) => state.cab)
  const [openFaq, setOpenFaq] = useState(0)
  const [discoverTab, setDiscoverTab] = useState("from")

  useEffect(() => initPmReveal(".city-view__section, .rp-section"), [loading, fromCityData, toCityData])

  // Content: prefer the live API sections, fall back to the build-time overrides
  // (real content baked for 30+ cities while the CMS API is unavailable).
  // deepClean strips em/en dashes from all rendered content (house style: no
  // em/en dashes anywhere), covering both the baked data and future API data.
  const fromSections = useMemo(
    () => deepClean(fromCityData?.sections || cityContentOverrides[slugify(fromCity)]?.sections || {}),
    [fromCityData, fromCity]
  )
  const toSections = useMemo(
    () => deepClean(toCityData?.sections || cityContentOverrides[slugify(toCity)]?.sections || {}),
    [toCityData, toCity]
  )

  const siblingRoutes = useMemo(() => getSiblingRoutes(fromCity, toCity, 12), [fromCity, toCity])
  const inboundRoutes = useMemo(() => getInboundRoutes(toCity, fromCity, 12), [fromCity, toCity])
  const hasReverse = useMemo(() => isTier1Route(toCity, fromCity), [fromCity, toCity])

  const facts = useMemo(() => getRouteFacts(fromCity, toCity), [fromCity, toCity])
  const routeDistanceKm = distance ? Math.ceil(distance) : facts?.distanceKm || null

  // Rough toll estimate from road distance: a labelled estimate, not a quote.
  // ~1.3/km blended for a car on national highways, rounded to 10. Deterministic
  // from the static distance so it renders into the prerendered HTML (no runtime
  // dependency). A precise per-plaza figure would come from a precompute job
  // (TollGuru / Google Routes) baked into routeFacts.json; this is the interim.
  const estTollCar = routeDistanceKm ? Math.round((routeDistanceKm * 1.3) / 10) * 10 : null

  // SEO title stays keyword-rich; comma, not an em dash.
  const heroTitle = `${fromCityName} to ${toCityName} Cab, Taxi, Car Rental & One-Way Hire`

  const introParagraphs = useMemo(() => {
    const fromIntro = fromSections.intro?.paragraphs?.[0] || ""
    return [
      `${fromCityName} to ${toCityName} is a popular outstation route. When you book a one-way cab from ${fromCityName} to ${toCityName}, you expect punctual service, clean vehicles, and drivers familiar with the road. Our ${fromCityName} to ${toCityName} cab service focuses on reliability, easy booking, and transparent pricing.`,
      fromIntro || `Whether you need a one-way cab from ${fromCityName} to ${toCityName} for business travel, family trips, or special occasions, we aim to keep every journey comfortable and safe.`,
    ]
  }, [fromCityName, toCityName, fromSections])

  const faqItems = useMemo(() => {
    const toFaqs = toSections.faq?.items || []
    const routeSpecificFaqs = [
      {
        q: `How do I book a cab from ${fromCityName} to ${toCityName}?`,
        a: `Enter your pickup and drop, date and time on the booking box, see the fixed all-inclusive fare for Hatchback, Sedan or SUV, then confirm with verified drivers.`,
      },
      {
        q: `Is one-way cab service available from ${fromCityName} to ${toCityName}?`,
        a: `Yes. One-way cabs from ${fromCityName} to ${toCityName} bill a single leg only, with transparent pricing and no return-fare padding.`,
      },
      {
        q: `What is the distance and time from ${fromCityName} to ${toCityName}?`,
        a: routeDistanceKm
          ? `${fromCityName} to ${toCityName} is about ${routeDistanceKm} km by road${facts?.duration ? `, and the drive usually takes around ${facts.duration}` : ""}. Actual time varies with traffic and road conditions.`
          : `Our drivers take the fastest available road route between ${fromCityName} and ${toCityName}. The exact distance and time for your pickup and drop are confirmed with your fare before you book.`,
      },
      {
        q: `Are tolls and parking included in the ${fromCityName} to ${toCityName} fare?`,
        a: `The fare shown includes driver allowance and GST. Tolls, parking and state permits are extra and billed at actuals.${estTollCar ? ` For ${fromCityName} to ${toCityName}, toll for a car is roughly ₹${estTollCar}, an estimate that varies by plaza and vehicle class.` : ""}`,
      },
    ]
    const all = [...routeSpecificFaqs]
    toFaqs.forEach((faq) => {
      if (!all.some((f) => f.q === faq.q)) all.push(faq)
    })
    return all.slice(0, 8)
  }, [fromCityName, toCityName, routeDistanceKm, estTollCar, facts, toSections])

  // Per-city discover guide, built from real baked content where present.
  const guideFor = (sections, name) => {
    const seeDo = [
      ...(sections.localAreas?.infoCards || []),
      ...(sections.travelIdeas?.cards || []),
    ]
    return {
      about: sections.intro?.paragraphs || [
        `${name} rewards a little exploring, and a cab on standby makes it easy. Your driver can wait while you take in the main sights, markets and food streets, then move you on when you are ready.`,
      ],
      seeDo,
      areas: sections.localAreas?.areas || [],
    }
  }
  const guides = {
    from: { name: fromCityName, ...guideFor(fromSections, fromCityName) },
    to: { name: toCityName, ...guideFor(toSections, toCityName) },
  }
  const activeGuide = guides[discoverTab]

  const goBook = () => {
    if (distance) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    } else {
      navigate("/", { state: { routePrefill: { fromCity, toCity } } })
    }
  }

  const jsonLdSchema = useMemo(() => {
    // Always the production origin, never window.location.origin: the prerender
    // step (scripts/seo/prerender.mjs) renders these pages against
    // http://127.0.0.1:4321, which was being baked straight into the breadcrumb
    // JSON-LD on every route page and invalidating its rich-result eligibility.
    const baseUrl = SITE_ORIGIN
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${baseUrl}/` },
            { "@type": "ListItem", position: 2, name: `${fromCityName} Cab Service`, item: `${baseUrl}/cities/${slugify(fromCity)}/` },
            { "@type": "ListItem", position: 3, name: `${fromCityName} to ${toCityName} Cab`, item: `${baseUrl}/routes/${slugify(fromCity)}-to-${slugify(toCity)}-cab/` },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: faqItems.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })),
        },
        {
          "@type": "Service",
          serviceType: "Outstation Cab Service",
          areaServed: [{ "@type": "City", name: fromCityName }, { "@type": "City", name: toCityName }],
          name: heroTitle,
          provider: { "@type": "Organization", name: "Pulpit Cab" },
        },
      ],
    }
  }, [fromCity, toCity, fromCityName, toCityName, faqItems, heroTitle])

  // Do not block the whole page on the CMS fetch: content comes from the baked
  // overrides + route facts immediately, and live API data (when it arrives)
  // simply enriches it. This keeps the page usable when the CMS is slow or down.
  return (
    <>
      <Helmet defer={false}>
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>

      <div className="rp">
        {/* ---------- hero ---------- */}
        <section className="rp-hero">
          <div className="rp-hero__bg" aria-hidden="true">
            <svg viewBox="0 0 1440 520" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="rp-hg" x1="0" y1="0" x2="0.4" y2="1">
                  <stop offset="0" stopColor="#178f89" />
                  <stop offset="1" stopColor="#0c5f5b" />
                </linearGradient>
                <radialGradient id="rp-glow" cx="0.86" cy="0.15" r="0.5">
                  <stop offset="0" stopColor="#ffd9a8" stopOpacity="0.16" />
                  <stop offset="1" stopColor="#ffd9a8" stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect width="1440" height="520" fill="url(#rp-hg)" />
              <rect width="1440" height="520" fill="url(#rp-glow)" />

              {/* chakra mandala, a quiet Indian motif that turns very slowly */}
              <g className="rp-art-spin" stroke="#ffd9a8" strokeOpacity="0.24" strokeWidth="2" fill="none">
                <circle cx="1250" cy="118" r="92" /><circle cx="1250" cy="118" r="58" /><circle cx="1250" cy="118" r="12" />
                <path d="M1342 118 H1158 M1329 164 L1171 72 M1296 197 L1204 39 M1250 210 V26 M1204 197 L1296 39 M1171 164 L1329 72" />
                <circle cx="1342" cy="118" r="3" /><circle cx="1158" cy="118" r="3" /><circle cx="1250" cy="210" r="3" /><circle cx="1250" cy="26" r="3" />
              </g>
              <g className="rp-art-spin-rev" stroke="#ffffff" strokeOpacity="0.09" strokeWidth="1.8" fill="none">
                <circle cx="150" cy="92" r="66" /><circle cx="150" cy="92" r="42" />
                <path d="M216 92 H84 M150 158 V26 M197 139 L103 45 M197 45 L103 139" />
              </g>

              {/* route currents */}
              <g fill="none" stroke="#ffffff" strokeOpacity="0.10" strokeWidth="2">
                <path d="M-50 380 C 300 300 520 440 820 360 S 1300 300 1500 380" />
                <path d="M-50 440 C 300 360 520 500 820 420 S 1300 360 1500 440" />
              </g>

              {/* faint Indian skyline line, temple shikhara, domed gate, minaret */}
              <g fill="none" stroke="#ffffff" strokeOpacity="0.11" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
                <path d="M250 476 V420 L288 380 L326 420 V476" /><path d="M288 380 V366" />
                <path d="M470 476 V412 Q470 384 502 384 Q534 384 534 412 V476" /><path d="M460 476 H544" /><path d="M502 384 V368" />
                <path d="M700 476 L716 476 L710 388 L706 388 Z" /><path d="M704 432 H712 M705 410 H711" /><path d="M708 388 Q702 376 714 388" />
              </g>

              {/* dashed intercity route threading location pins */}
              <g fill="none" stroke="#ffd9a8" strokeOpacity="0.5" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="1.5 12">
                <path d="M120 250 C 360 190 560 300 760 236 S 1160 190 1320 262" />
              </g>
              <g fill="#ffd9a8" fillOpacity="0.4">
                <g transform="translate(120,250)"><path d="M0 0 C-7 -10 -7 -18 0 -18 C7 -18 7 -10 0 0 Z" /></g>
                <g transform="translate(760,236)"><path d="M0 0 C-7 -10 -7 -18 0 -18 C7 -18 7 -10 0 0 Z" /></g>
                <g transform="translate(1320,262)"><path d="M0 0 C-7 -10 -7 -18 0 -18 C7 -18 7 -10 0 0 Z" /></g>
              </g>
            </svg>
          </div>

          <div className="rp-wrap rp-hero__in">
            <span className="rp-eyebrow">Outstation &middot; one way &amp; round trip</span>
            <h1 className="rp-hero__title">{heroTitle}</h1>
            <p className="rp-hero__lead">
              Your {fromCityName} to {toCityName} cab at a fixed, all-inclusive fare. Verified drivers,
              clean cars, and the price shown upfront.
            </p>

            <div className="rp-hero__facts">
              {routeDistanceKm && <span className="rp-hero__fact">{I.road}~{routeDistanceKm} km</span>}
              {facts?.duration && <span className="rp-hero__fact">{I.clock}~{facts.duration}</span>}
              {facts?.startingPrice && <span className="rp-hero__fact">{I.rupee}from {facts.startingPrice}</span>}
            </div>

            <button className="rp-btn rp-btn--onteal" onClick={goBook}>
              {distance ? "See fares & book" : `Book ${fromCityName} to ${toCityName} cab`} {I.arrow}
            </button>
          </div>
        </section>

        <div className="rp-wrap">
          {/* ---------- what your fare includes ---------- */}
          <section className="rp-section">
            <div className="rp-head">
              <span className="rp-kicker">Fixed, transparent price</span>
              <h2 className="rp-h2">What your {fromCityName} to {toCityName} fare includes</h2>
            </div>
            <div className="rp-includes">
              {FARE_INCLUDES.map((f) => (
                <div className="rp-inc" key={f.t}>
                  <span className="rp-inc__ic">{I[f.k]}</span>
                  <h3 className="rp-inc__t">{f.t}</h3>
                  <p className="rp-inc__d">{f.d}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ---------- the journey ---------- */}
          <section className="rp-section">
            <div className="rp-journey">
              <div className="rp-journey__copy">
                <span className="rp-kicker">The journey</span>
                <h2 className="rp-h2">{fromCityName} to {toCityName}, and we travel with you</h2>
                <p className="rp-p">
                  {routeDistanceKm
                    ? `It is about ${routeDistanceKm} km${facts?.duration ? ` and roughly ${facts.duration}` : ""} on the road. `
                    : ""}
                  Your driver stays with you the whole trip, so you set the pace, quick refreshment and
                  rest stops along the way are free.
                </p>
                <p className="rp-p">
                  <strong>Best time to travel:</strong> early mornings and late evenings are easiest for
                  traffic; weekends see higher demand.
                </p>
                <div className="rp-weather-row"><WeatherChip city={toCityName} /></div>
                {hasReverse && (
                  <p className="rp-p">
                    <strong>Coming back?</strong>{" "}
                    <Link to={routePath(toCity, fromCity)} className="rp-link">
                      Book a {toCityName} to {fromCityName} cab
                    </Link>{" "}
                    for the return leg.
                  </p>
                )}
              </div>
              <div className="rp-journey__stats">
                {routeDistanceKm && (
                  <div className="rp-stat"><span className="rp-stat__v">{routeDistanceKm}</span><span className="rp-stat__l">km by road</span></div>
                )}
                {facts?.duration && (
                  <div className="rp-stat"><span className="rp-stat__v">{facts.duration}</span><span className="rp-stat__l">typical drive</span></div>
                )}
                {facts?.startingPrice && (
                  <div className="rp-stat"><span className="rp-stat__v">{facts.startingPrice}</span><span className="rp-stat__l">fares from</span></div>
                )}
                <div className="rp-stat"><span className="rp-stat__v">3</span><span className="rp-stat__l">cab options</span></div>
                {estTollCar && (
                  <div className="rp-stat"><span className="rp-stat__v">~₹{estTollCar}</span><span className="rp-stat__l">est. toll, at actuals</span></div>
                )}
              </div>
            </div>
          </section>

          {/* ---------- why book ---------- */}
          <section className="rp-section rp-prose">
            <div className="rp-head">
              <span className="rp-kicker">Why Pulpit</span>
              <h2 className="rp-h2">Why book {fromCityName} to {toCityName} with Pulpit?</h2>
            </div>
            {introParagraphs.map((p, i) => <p className="rp-p" key={i}>{p}</p>)}
          </section>

          {/* ---------- discover both cities ---------- */}
          <section className="rp-section">
            <div className="rp-head">
              <span className="rp-kicker">{I.compass}Explore</span>
              <h2 className="rp-h2">Discover {fromCityName} &amp; {toCityName}</h2>
              <p className="rp-sub">Both ends of the route, with your cab on standby.</p>
            </div>

            <div className="rp-tabs" role="tablist" aria-label="Discover city">
              <button className={`rp-tab${discoverTab === "from" ? " is-on" : ""}`} onClick={() => setDiscoverTab("from")} role="tab" aria-selected={discoverTab === "from"}>
                {fromCityName}
              </button>
              <button className={`rp-tab${discoverTab === "to" ? " is-on" : ""}`} onClick={() => setDiscoverTab("to")} role="tab" aria-selected={discoverTab === "to"}>
                {toCityName}
              </button>
            </div>

            <div className="rp-discover" key={discoverTab}>
              {activeGuide.about?.[0] && <p className="rp-p rp-discover__about">{activeGuide.about[0]}</p>}

              {activeGuide.seeDo.length > 0 && (
                <div className="rp-cards">
                  {activeGuide.seeDo.map((c, i) => (
                    <div className="rp-card" key={i}>
                      <span className="rp-card__ic">{I.pin}</span>
                      <h3 className="rp-card__t">{c.title}</h3>
                      <p className="rp-card__d">{c.text}</p>
                    </div>
                  ))}
                </div>
              )}

              {activeGuide.areas.length > 0 && (
                <div className="rp-areas">
                  <span className="rp-areas__lab">Pickup &amp; drop across {activeGuide.name}</span>
                  <div className="rp-areas__chips">
                    {activeGuide.areas.map((a) => <span className="rp-chip" key={a}>{a}</span>)}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ---------- faq ---------- */}
          {faqItems.length > 0 && (
            <section className="rp-section">
              <div className="rp-head">
                <span className="rp-kicker">Good to know</span>
                <h2 className="rp-h2">{fromCityName} to {toCityName} FAQs</h2>
              </div>
              <div className="rp-faqs">
                {faqItems.map((faq, i) => {
                  const open = openFaq === i
                  return (
                    <div className={`rp-faq${open ? " is-open" : ""}`} key={i}>
                      <button className="rp-faq__q" onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open}>
                        <span>{faq.q}</span>
                        <span className="rp-faq__pm" aria-hidden="true" />
                      </button>
                      {open && <p className="rp-faq__a">{faq.a}</p>}
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* ---------- ready to book ---------- */}
          <section className="rp-section">
            <div className="rp-cta">
              <div className="rp-cta__art" aria-hidden="true">
                <svg viewBox="0 0 1200 300" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="rp-cg" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#178f89" /><stop offset="1" stopColor="#0b524e" />
                    </linearGradient>
                  </defs>
                  <rect width="1200" height="300" fill="url(#rp-cg)" />
                  <g fill="none" stroke="#ffd9a8" strokeOpacity=".22" strokeWidth="2" strokeDasharray="2 12" strokeLinecap="round"><path d="M60 210 H1140" /></g>
                </svg>
              </div>
              <div className="rp-cta__in">
                <div>
                  <h2 className="rp-cta__title">Ready to ride to {toCityName}?</h2>
                  <p className="rp-cta__text">See the fixed fare for Hatchback, Sedan and SUV, and book in about a minute.</p>
                </div>
                <button className="rp-btn rp-btn--onteal rp-btn--lg" onClick={goBook}>
                  {distance ? "See fares & book" : `Book ${fromCityName} to ${toCityName}`} {I.arrow}
                </button>
              </div>
            </div>
          </section>

          {/* ---------- other routes & city cabs ---------- */}
          <section className="rp-section">
              <div className="rp-head">
                <span className="rp-kicker">Keep exploring</span>
                <h2 className="rp-h2">Other routes &amp; city cabs</h2>
              </div>

              <div className="rp-links">
                <Link className="rp-link-chip rp-link-chip--city" to={cityPath(fromCity)}>Cabs in {fromCityName} {I.arrow}</Link>
                <Link className="rp-link-chip rp-link-chip--city" to={cityPath(toCity)}>Cabs in {toCityName} {I.arrow}</Link>
              </div>

              {siblingRoutes.length > 0 && (
                <div className="rp-links__group">
                  <h3 className="rp-links__h">Other cab routes from {fromCityName}</h3>
                  <div className="rp-links">
                    {siblingRoutes.map((c) => (
                      <Link className="rp-link-chip" key={c} to={routePath(fromCity, c)} title={`${fromCityName} to ${titleCase(c)} Cab`}>
                        {fromCityName} to {titleCase(c)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {inboundRoutes.length > 0 && (
                <div className="rp-links__group">
                  <h3 className="rp-links__h">Cab routes to {toCityName} from other cities</h3>
                  <div className="rp-links">
                    {inboundRoutes.map((c) => (
                      <Link className="rp-link-chip" key={c} to={routePath(c, toCity)} title={`${titleCase(c)} to ${toCityName} Cab`}>
                        {titleCase(c)} to {toCityName}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </section>
        </div>
      </div>
    </>
  )
}

export default CityPairView
